/**
 * Questions and answers of the FAQ page (/faq).
 *
 * Cancellations and payments follow the current terms (CGV, /terms-of-service): keep both in sync.
 * Fees and commission rates are not written here on purpose, as they may change with the
 * trainer plans.
 */

export const CONTACT_EMAIL = 'contact@formabel.be';

// The 4 entry points at the top of the page, each pointing to a section
export const TOPICS = [
  { id: 'apprenants', icon: 'learner', title: 'Apprenants', text: 'Réserver, payer, avis' },
  { id: 'formateurs', icon: 'trainer', title: 'Formateurs', text: 'Publier, être payé' },
  { id: 'annulations', icon: 'cancel', title: 'Annulations', text: 'Délais, remboursements' },
  { id: 'compte', icon: 'account', title: 'Compte & données', text: 'Sécurité, réclamations' },
];

export const POPULAR = [
  { label: 'Annuler une réservation', question: 'annuler-reservation' },
  { label: 'Payer en plusieurs fois', question: 'plusieurs-fois' },
  { label: 'Publier une formation', question: 'proposer-formations' },
];

// Refunds to a learner who cancels, from the CGV
export const REFUNDS = [
  { when: "Jusqu'à 48 h avant le début de la formation", refund: 'Remboursé à 100 %', full: true },
  {
    when: 'Moins de 48 h avant, ou absence non justifiée',
    refund: 'Remboursé à 50 %',
    full: false,
  },
];

/**
 * Each answer is a list of blocks: { p }, { steps }, { refunds: true } or
 * { link: { name, label } }.
 */
export const SECTIONS = [
  {
    id: 'apprenants',
    title: 'Réserver une formation',
    intro: "Tout ce qu'il faut savoir avant de vous inscrire.",
    questions: [
      {
        id: 'reserver',
        q: 'Comment réserver une formation sur Formabel ?',
        a: [
          {
            steps: [
              'Créez votre compte gratuit avec votre adresse e-mail.',
              'Choisissez une formation, puis la session qui vous convient (date et horaire).',
              'Indiquez le nombre de places et payez en ligne, en une fois ou en plusieurs.',
            ],
          },
          {
            p:
              'Vous recevez aussitôt la confirmation par e-mail, avec le lieu, les horaires et les coordonnées du formateur. Il ne vous reste plus qu’à venir le jour J.',
          },
          { link: { name: 'SearchPage', label: 'Voir les formations' } },
        ],
      },
      {
        id: 'question-formateur',
        q: 'Puis-je poser une question au formateur avant de réserver ?',
        a: [
          {
            p:
              'Oui. Sur la fiche de la formation, contactez le formateur par la messagerie Formabel : niveau requis, matériel à apporter, accès au lieu… Il vous répond directement.',
          },
        ],
      },
      {
        id: 'plusieurs-places',
        q: 'Puis-je réserver pour plusieurs personnes ou pour mon entreprise ?',
        a: [
          {
            p:
              'Oui, choisissez le nombre de places au moment de réserver, dans la limite des places restantes. Pour un groupe plus important ou une formation sur mesure dans votre entreprise, écrivez au formateur ou à notre équipe : nous vous mettons en relation.',
          },
        ],
      },
      {
        id: 'prix',
        q: 'Les prix sont-ils les mêmes que chez le formateur ?',
        a: [
          {
            p:
              "Oui : le prix de la formation est fixé par le formateur lui-même et affiché sur la fiche. Des frais de service Formabel, qui financent la plateforme et le paiement sécurisé, s'y ajoutent. Leur montant exact est toujours affiché avant de payer, sans surprise.",
          },
        ],
      },
      {
        id: 'attestation',
        q: 'Vais-je recevoir une attestation ?',
        a: [
          {
            p:
              'La plupart des formateurs remettent une attestation de participation à la fin de la formation. Ce qui est remis est indiqué sur la fiche de la formation ; en cas de doute, posez la question au formateur avant de réserver.',
          },
        ],
      },
      {
        id: 'formateur-serieux',
        q: 'Comment savoir si un formateur est sérieux ?',
        a: [
          {
            p:
              'Lisez les avis : seuls les apprenants qui ont réservé et suivi la formation sur Formabel peuvent en laisser un. Ce sont donc de vrais avis, impossibles à acheter.',
          },
          {
            p:
              "Regardez aussi le badge « Formateur vérifié » : Formabel a contrôlé le numéro d'entreprise du formateur et que son activité relève bien de l'enseignement et de la formation. Chaque annonce est en plus relue par notre équipe avant d'être publiée.",
          },
        ],
      },
      {
        id: 'avis',
        q: 'Comment laisser un avis après ma formation ?',
        a: [
          {
            p:
              'Après la formation, vous recevez une invitation par e-mail. Vous pouvez aussi vous connecter, ouvrir votre réservation dans « Boîte de réception » et cliquer sur « Laisser un avis ». Donnez une note de 1 à 5 étoiles et quelques mots : votre avis aide les autres apprenants et le formateur.',
          },
        ],
      },
    ],
  },
  {
    id: 'paiement',
    title: 'Paiement',
    intro: 'Paiement sécurisé par Stripe, en une fois ou en plusieurs.',
    questions: [
      {
        id: 'paiement',
        q: 'Comment fonctionne le paiement ?',
        a: [
          {
            p:
              "Vous payez en ligne au moment de réserver, sur une page sécurisée par Stripe, le leader du paiement en ligne. Formabel n'a jamais accès à vos données de carte.",
          },
          {
            p:
              "Votre argent est protégé : le formateur n'est payé qu'après la formation, une fois votre présence validée. Si la formation est annulée, vous êtes remboursé.",
          },
        ],
      },
      {
        id: 'plusieurs-fois',
        q: 'Puis-je payer en plusieurs fois ?',
        a: [
          {
            p:
              "Oui, avec Scalapay : en 3 ou 4 fois sans intérêts. Choisissez Scalapay au moment de payer ; le montant de chaque échéance est indiqué sur la fiche de la formation. Des frais de service Scalapay peuvent s'appliquer.",
          },
        ],
      },
      {
        id: 'facture',
        q: 'Puis-je obtenir une facture ?',
        a: [
          {
            p: `Oui. Le reçu de paiement vous est envoyé par e-mail. Pour une facture au nom de votre entreprise, avec votre numéro de TVA, écrivez-nous à ${CONTACT_EMAIL} en indiquant votre réservation.`,
          },
        ],
      },
    ],
  },
  {
    id: 'annulations',
    title: 'Annulations et absences',
    intro: "Des règles simples, connues à l'avance.",
    questions: [
      {
        id: 'annuler-reservation',
        q: 'Puis-je annuler ma réservation ?',
        a: [
          { p: 'Oui. Le remboursement dépend du moment où vous annulez :' },
          { refunds: true },
          {
            p: `Pour annuler, écrivez-nous à ${CONTACT_EMAIL} avec le nom de la formation et la date, ou prévenez le formateur par la messagerie de votre réservation. Le remboursement est fait par Stripe, sur le moyen de paiement utilisé, en général sous 5 à 10 jours.`,
          },
          { link: { name: 'TermsOfServicePage', label: 'Lire les conditions complètes' } },
        ],
      },
      {
        id: 'formateur-annule',
        q: 'Et si le formateur annule la formation ?',
        a: [
          {
            p:
              'Vous êtes remboursé intégralement, automatiquement, et prévenu par e-mail. Si le formateur propose une autre date, vous pouvez réserver cette nouvelle session ou choisir une autre formation.',
          },
        ],
      },
      {
        id: 'malade',
        q: 'Je suis malade le jour de la formation, que faire ?',
        a: [
          {
            p: `Prévenez le formateur par la messagerie de votre réservation et écrivez-nous à ${CONTACT_EMAIL}, le plus tôt possible.`,
          },
          {
            p:
              "Envoyez-nous un certificat médical : une absence justifiée n'est pas traitée comme une absence sans prévenir. Nous trouvons alors une solution avec le formateur, en priorité une place sur une prochaine session, sans frais.",
          },
        ],
      },
      {
        id: 'retard',
        q: 'Je suis en retard, que faire ?',
        a: [
          {
            p:
              'Prévenez le formateur dès que possible par la messagerie ou au numéro communiqué dans votre confirmation. Les formations sont pratiques et en petit groupe : un gros retard peut vous faire manquer une partie importante.',
          },
        ],
      },
    ],
  },
  {
    id: 'formateurs',
    title: 'Pour les formateurs',
    intro: 'Publier, remplir vos sessions et être payé.',
    questions: [
      {
        id: 'proposer-formations',
        q: 'Comment proposer mes formations sur Formabel ?',
        a: [
          {
            steps: [
              'Créez gratuitement votre compte formateur.',
              'Publiez une annonce : programme, photos, lieu, prix, dates, horaires et nombre de places.',
              "Connectez votre compte de paiement Stripe pour recevoir l'argent de vos réservations.",
              "Notre équipe relit l'annonce et vous informe dès qu'elle est validée.",
            ],
          },
          {
            p:
              'La publication est gratuite : vous ne payez une commission que lorsque vous remplissez vos places.',
          },
          { link: { name: 'BecomeTrainerPage', label: 'Devenir formateur' } },
        ],
      },
      {
        id: 'visible',
        q: 'Combien de temps avant que ma formation soit visible ?',
        a: [
          {
            p:
              "Notre équipe relit chaque annonce, en général sous 48 h ouvrables. Dès qu'elle est validée, elle est visible sur le site et les apprenants peuvent réserver. Si quelque chose manque, nous vous disons quoi corriger.",
          },
        ],
      },
      {
        id: 'modifier',
        q: 'Puis-je modifier ma formation moi-même ?',
        a: [
          {
            p:
              'Oui, depuis votre tableau de bord : description, photos, prix et sessions. Vous pouvez ajouter de nouvelles dates à tout moment. Gardez vos informations à jour pour éviter les mauvaises surprises.',
          },
        ],
      },
      {
        id: 'paye',
        q: 'Quand et comment suis-je payé ?',
        a: [
          {
            p:
              'Par virement automatique via Stripe, sur votre compte bancaire. Après la formation, validez la présence de vos apprenants sur Formabel : le paiement part alors, commission Formabel déduite.',
          },
        ],
      },
      {
        id: 'badge',
        q: 'Comment obtenir le badge « Formateur vérifié » ?',
        a: [
          {
            p:
              "Envoyez-nous votre numéro de TVA (ou numéro d'entreprise). Nous vérifions que votre activité est enregistrée sous un code NACEBEL de la catégorie 85, « Enseignement » (par exemple 85.59 : autres formes d'enseignement). Si c'est le cas, le badge « Formateur vérifié » s'affiche sur votre profil et vos annonces.",
          },
          {
            p:
              'Bientôt : le badge « Formateur spécialisé », encore plus visible et reconnu par les apprenants, sera réservé aux formateurs abonnés à Formabel Premium.',
          },
          {
            p:
              'Sans badge, vous pouvez quand même publier vos formations : votre profil indique simplement « Formateur non vérifié ».',
          },
        ],
      },
      {
        id: 'absent',
        q: 'Que se passe-t-il si un apprenant ne vient pas ?',
        a: [
          {
            p:
              "Au moment de valider les présences, indiquez l'apprenant comme absent. S'il n'a pas prévenu, l'absence n'est pas justifiée : il n'est remboursé qu'à 50 % et vous recevez l'autre moitié, commission déduite. Votre place n'est donc pas perdue pour rien.",
          },
          {
            p:
              "Si l'apprenant justifie son absence (certificat médical), nous vous proposons en priorité de l'accueillir sur une prochaine session.",
          },
        ],
      },
      {
        id: 'annuler-formation',
        q: 'Je dois annuler une session, comment faire ?',
        a: [
          {
            p: `Prévenez vos apprenants par la messagerie et écrivez-nous à ${CONTACT_EMAIL}, le plus tôt possible. Les apprenants sont remboursés intégralement. Proposez-leur une autre date : beaucoup réservent à nouveau.`,
          },
          {
            p:
              'Des annulations répétées ou injustifiées peuvent entraîner la suspension du compte, pour protéger la confiance des apprenants.',
          },
        ],
      },
      {
        id: 'abonnements',
        q: 'Faut-il prendre un abonnement pour publier ?',
        a: [
          {
            p:
              'Non, publier sur Formabel est et restera gratuit. Des abonnements formateurs arrivent bientôt, en option et sans engagement : plus de visibilité, mise en avant de vos formations, badges et commission réduite.',
          },
        ],
      },
    ],
  },
  {
    id: 'compte',
    title: 'Compte et données',
    intro: 'Vos informations, votre sécurité.',
    questions: [
      {
        id: 'donnees',
        q: 'Comment mes données sont-elles utilisées ?',
        a: [
          {
            p:
              'Uniquement pour traiter vos réservations et vous mettre en relation avec le formateur : il reçoit votre nom et vos coordonnées pour organiser la formation. Vos données sont traitées dans le respect du RGPD et hébergées dans des services sécurisés.',
          },
          {
            link: { name: 'PrivacyPolicyPage', label: 'Lire la politique de confidentialité' },
          },
        ],
      },
      {
        id: 'supprimer-compte',
        q: 'Comment modifier ou supprimer mon compte ?',
        a: [
          {
            p: `Modifiez vos informations à tout moment dans « Paramètres du compte ». Pour supprimer votre compte et vos données, écrivez-nous à ${CONTACT_EMAIL} : nous le faisons rapidement, une fois vos réservations en cours terminées.`,
          },
        ],
      },
      {
        id: 'reclamation',
        q: 'Où puis-je adresser une réclamation ?',
        a: [
          {
            p: `Écrivez-nous à ${CONTACT_EMAIL} en expliquant la situation, avec le nom de la formation et la date. Une personne de l'équipe lit chaque message et vous répond, que vous soyez apprenant ou formateur. Nous cherchons toujours une solution juste pour les deux parties.`,
          },
        ],
      },
    ],
  },
];
